import { Container, Sprite, Text, type Texture } from 'pixi.js';
import { TILE_PX } from './constants.js';
import type { AssetBundle } from './assets.js';

/**
 * The tint pipeline (TRD-03 §5) — the visually load-bearing part of the renderer.
 *
 * Every tower on the board shares one 16×16 silhouette (GDD §2), so tint and glyph carry
 * 100% of identity. A tower is four sprites:
 *
 *   base  ← class A colour     ring  ← class B accent
 *   glyph ← white              stars ← only when star > 1
 *
 * The base and ring MUST occupy different pixels, or the second class is simply invisible
 * and half the synergy information never reaches the player.
 */

export interface TowerVisual {
  defId: string;
  colorA: number;
  colorB: number;
  glyph: string;
  star: 1 | 2 | 3;
}

export class TowerView {
  readonly container = new Container();

  private readonly base: Sprite;
  private readonly ring: Sprite;
  private readonly glyph: Text;
  private readonly star: Sprite;
  private readonly outline: Sprite;

  /** Cached so tints are applied once at creation, never per frame (TRD-03 R-03-08). */
  private lastKey = '';

  private fireTween = 0;
  private recoilX = 0;
  private recoilY = 0;

  /** B-5: a pulsing gold ★ when this tower's merge is ready — fed by the renderer. */
  private readonly mergeHint: Text;
  private hintTime = 0;

  constructor(private readonly assets: AssetBundle) {
    this.outline = new Sprite(assets.sprite['tower_outline'] as Texture);
    this.base = new Sprite(assets.sprite['tower_base'] as Texture);
    this.ring = new Sprite(assets.sprite['tower_ring'] as Texture);
    this.star = new Sprite(assets.sprite['star_1'] as Texture);

    // A BitmapFont would be better here (TRD-03 R-03-09) — Text allocates a canvas and a
    // texture per instance. Deferred until the real pixel font exists (TRD-12 §7); with
    // ~40 towers the cost is a one-time allocation, not a per-frame one.
    this.glyph = new Text({
      text: '',
      style: { fontFamily: 'monospace', fontSize: 7, fill: 0xffffff },
    });
    this.glyph.anchor.set(0.5, 0.5);
    this.glyph.position.set(TILE_PX / 2, TILE_PX / 2 + 1);

    this.outline.visible = false;
    this.star.visible = false;
    this.star.position.set(10, 10);

    this.mergeHint = new Text({
      text: '★',
      style: { fontFamily: 'monospace', fontSize: 7, fill: 0xe8c95a, fontWeight: 'bold' },
    });
    this.mergeHint.anchor.set(0.5, 0.5);
    this.mergeHint.position.set(TILE_PX - 2, 1);
    this.mergeHint.visible = false;

    this.container.addChild(this.outline, this.base, this.ring, this.glyph, this.star, this.mergeHint);
    this.container.pivot.set(TILE_PX / 2, TILE_PX / 2);
  }

  apply(visual: TowerVisual): void {
    const key = `${visual.defId}|${visual.colorA}|${visual.colorB}|${visual.star}`;
    if (key === this.lastKey) return;
    this.lastKey = key;

    this.base.tint = visual.colorA;
    this.ring.tint = visual.colorB;
    this.glyph.text = visual.glyph;

    if (visual.star > 1) {
      this.star.texture = this.assets.sprite[`star_${visual.star}`] as Texture;
      this.star.visible = true;
    } else {
      this.star.visible = false;
    }
  }

  setTile(tileX: number, tileY: number): void {
    this.container.position.set(tileX * TILE_PX + TILE_PX / 2, tileY * TILE_PX + TILE_PX / 2);
    this.container.zIndex = tileY;
  }

  /**
   * Knocked out by a Disabler (playtest request).
   *
   * Dimmed hard rather than tinted: the Disabler's reach is invisible, so the STATE has to
   * be unmistakable at a glance or a tower that stopped firing reads as a bug. Applied to
   * the whole container so the glyph, ring and star all go down together — a half-lit
   * tower would look like a rendering fault instead of a disabled one.
   */
  setDisabled(on: boolean): void {
    this.container.alpha = on ? 0.35 : 1;
  }

  setMergeHint(on: boolean): void {
    this.mergeHint.visible = on;
  }

  setHighlight(mode: 'none' | 'hover' | 'selected'): void {
    // A pre-baked outline sprite, not a Filter. Filters are a per-object render pass; at
    // 40 towers they would cost more than everything else combined (TRD-03 R-03-11).
    this.outline.visible = mode !== 'none';
    this.outline.alpha = mode === 'selected' ? 1 : 0.6;
  }

  /**
   * Fire reaction (TRD-03 R-03-10): a 1.25× pop plus a 1px recoil AWAY from the target.
   * The cheapest possible "the board is alive" signal, and it matters enormously when
   * every tower is the same shape.
   */
  onFired(targetX: number, targetY: number): void {
    this.fireTween = 1;
    const dx = targetX * TILE_PX - this.container.x;
    const dy = targetY * TILE_PX - this.container.y;
    const len = Math.hypot(dx, dy) || 1;
    this.recoilX = (-dx / len) * 1.5;
    this.recoilY = (-dy / len) * 1.5;
  }

  /** @param deltaMs real elapsed time — presentation is never tick-locked (TRD-04 R-04-02). */
  update(deltaMs: number, tileX: number, tileY: number): void {
    if (this.mergeHint.visible) {
      this.hintTime += deltaMs;
      this.mergeHint.alpha = 0.55 + 0.45 * Math.sin(this.hintTime / 180);
    }
    if (this.fireTween <= 0) return;

    this.fireTween = Math.max(0, this.fireTween - deltaMs / 90);
    const t = this.fireTween;
    const scale = 1 + 0.25 * t;
    this.container.scale.set(scale);
    this.container.position.set(
      Math.round(tileX * TILE_PX + TILE_PX / 2 + this.recoilX * t),
      Math.round(tileY * TILE_PX + TILE_PX / 2 + this.recoilY * t),
    );

    if (this.fireTween === 0) {
      this.container.scale.set(1);
      this.setTile(tileX, tileY);
    }
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }
}
